/**
 * @fileoverview Hook for managing project-specific AI agent configurations.
 *
 * Handles synchronization between global agent definitions and project-specific
 * settings, including automatic addition of new agents and removal of stale ones.
 */

'use client';

import { useState, useCallback, useEffect, useMemo } from 'react';
import { Project } from '@/domains/projects/schemas';
import { ProjectAgent, AgentUpdate } from '@/domains/agents';
import { useToast } from '@/hooks/use-toast';
import { useLogs } from '@/hooks/use-logs';
import { AGENT_DEFINITIONS } from '@/ai/agents/definitions';
import { resolveDefaultModelConfig } from '@/domains/agents/presets/apply-preset';
import { setAgent } from '@/domains/agents/services/set-agent';
import { setAllAgents as setAllAgentsOnServer } from '@/domains/agents/services/set-all-agents';
import { removeAgentsBatch } from '../services/remove-agents-batch';

/**
 * A custom hook to manage project-specific agent configurations.
 * This encapsulates all logic for fetching, updating, and preparing agents
 * within a given project, including optimistic updates and server persistence.
 *
 * @param project The full project object.
 * @param setProject The state setter function for the project from the parent context.
 * @returns An object containing the list of agents and functions to interact with them.
 */
export function useProjectAgents(
  project: Project,
  setProject: React.Dispatch<React.SetStateAction<Project>>
) {
  const { toast } = useToast();
  const agents = useMemo(() => project.agents || [], [project.agents]);

  // Effect to synchronize agents in the project with the global definitions.
  // This handles both adding new agents and removing stale ones.
  useEffect(() => {
    const definedAgentIds = new Set(AGENT_DEFINITIONS.map(def => def.id));
    const projectAgentIds = new Set(agents.map(a => a.id));

    const missingAgentDefs = AGENT_DEFINITIONS.filter(def => !projectAgentIds.has(def.id));
    const staleAgents = agents.filter(agent => !definedAgentIds.has(agent.id));

    if (missingAgentDefs.length === 0 && staleAgents.length === 0) {
      return; // No changes needed
    }

    let previousAgents: ProjectAgent[] = [];
    let updatedAgents: ProjectAgent[] = [];
    let toastMessages: string[] = [];

    setProject(prev => {
      previousAgents = prev.agents || [];
      updatedAgents = [...previousAgents];

      // Add missing agents. New records carry overrides only — model
      // settings now, optional `promptOverride` later if the user edits.
      // No snapshot of the prompt (the executor reads
      // `agent.defaultPrompt` live, so framework updates propagate).
      if (missingAgentDefs.length > 0) {
        const newAgentsToAdd: ProjectAgent[] = missingAgentDefs.map(def => {
          const defaultModel = resolveDefaultModelConfig(def.id);
          return {
            id: def.id,
            name: def.name,
            description: def.description,
            modelProvider: defaultModel.modelProvider,
            modelName: defaultModel.modelName,
            path: '', // Deprecated
            category: def.category,
          };
        });
        updatedAgents.push(...newAgentsToAdd);
        toastMessages.push(`${newAgentsToAdd.length} agent(s) added.`);
        console.log(`[useProjectAgents] Found ${newAgentsToAdd.length} new agents to add.`);
      }

      // Remove stale agents
      if (staleAgents.length > 0) {
        const staleAgentIds = new Set(staleAgents.map(a => a.id));
        updatedAgents = updatedAgents.filter(agent => !staleAgentIds.has(agent.id));
        toastMessages.push(`${staleAgents.length} agent(s) removed.`);
        console.log(`[useProjectAgents] Found ${staleAgents.length} stale agents to remove.`);
      }

      console.log('[useProjectAgents] Applying optimistic update for agent sync.');
      return { ...prev, agents: updatedAgents };
    });

    // Show a combined toast message
    if (toastMessages.length > 0) {
        toast({
            title: "Agent Configuration Synced",
            description: toastMessages.join(' '),
        });
    }

    // Persist changes to Firestore
    const persistencePromises: Promise<void>[] = [];

    if (missingAgentDefs.length > 0) {
        const agentsToUpdate = updatedAgents.filter(agent => !previousAgents.some(oa => oa.id === agent.id));
        persistencePromises.push(setAllAgentsOnServer(project.id, agentsToUpdate));
    }

    if (staleAgents.length > 0) {
        const staleAgentIds = staleAgents.map(a => a.id);
        persistencePromises.push(removeAgentsBatch(project.id, staleAgentIds));
    }

    Promise.all(persistencePromises).catch(e => {
        console.error(`Failed to synchronize agents to Firestore, rolling back UI.`, e);
        setProject(prev => ({ ...prev, agents: previousAgents }));
        toast({
            variant: 'destructive',
            title: 'Agent Sync Failed',
            description: 'Could not synchronize agent configurations to the project.',
        });
    });

  }, [agents, project.id, setProject, toast]);

  // `ProjectAgent` no longer carries the prompt as a stored field; consumers
  // wanting the effective prompt should call `resolveProjectAgentPrompt`
  // (in `@/domains/agents`), which picks `promptOverride` when present and
  // falls back to `agent.defaultPrompt` (live code) otherwise.
  const getAgent = useCallback((agentId: string): ProjectAgent | undefined => {
    return agents.find(a => a.id === agentId);
  }, [agents]);

  const updateAgent = useCallback(async (agentId: string, updates: AgentUpdate) => {
    let updatedAgent: ProjectAgent | undefined;
    let previousAgents: ProjectAgent[] = [];

    setProject(prev => {
      previousAgents = prev.agents || [];
      const agentToUpdate = previousAgents.find(a => a.id === agentId);
      if (!agentToUpdate) return prev;

      updatedAgent = { ...agentToUpdate, ...updates };
      return {
        ...prev,
        agents: previousAgents.map(agent => agent.id === agentId ? updatedAgent! : agent),
        updatedAt: new Date().toISOString(),
      };
    });

    if (!updatedAgent) {
      throw new Error(`Agent with ID ${agentId} not found.`);
    }

    try {
      await setAgent(project.id, updatedAgent);
    } catch (error) {
      console.error(`Firestore update for agent ${agentId} failed, rolling back:`, error);
      setProject(prev => ({ ...prev, agents: previousAgents }));
      toast({ variant: 'destructive', title: 'Agent Update Failed', description: `Could not save changes for ${updatedAgent!.name}.` });
      throw error;
    }
  }, [project.id, setProject, toast]);

  const setAllAgents = useCallback(async (newAgents: ProjectAgent[]): Promise<void> => {
    let previousAgents: ProjectAgent[] = [];
    setProject(prev => {
      previousAgents = prev.agents || [];
      return { ...prev, agents: newAgents, updatedAt: new Date().toISOString() };
    });

    try {
        await setAllAgentsOnServer(project.id, newAgents);
    } catch (error) {
        console.error("Firestore batch update for agents failed, rolling back:", error);
        setProject(prev => ({ ...prev, agents: previousAgents }));
        toast({ variant: 'destructive', title: 'Agent Update Failed', description: 'Could not save all agent configurations.' });
        throw error;
    }
  }, [project.id, setProject, toast]);

  const prepareAgent = useCallback(async (agentId: string): Promise<ProjectAgent | undefined> => {
    const agent = getAgent(agentId);
    if (!agent) {
        console.error(`[prepareAgent] Agent with ID ${agentId} not found in project context.`);
        toast({ variant: 'destructive', title: 'Agent Not Found', description: `The agent "${agentId}" is missing.` });
        return undefined;
    }
    return agent;
  }, [getAgent, toast]);

  return {
    agents,
    getAgent,
    updateAgent,
    setAllAgents,
    prepareAgent,
  };
}
